import * as VideoProcessors from '@twilio/video-processors';
import cx from 'classnames';
import {filter, find, includes, isEmpty, map, merge, some} from 'lodash';
import {Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, Dropdown, DropdownItem, Icon, IconName, IconColor, Square, Tooltip, TestAttribute} from 'components';
import {ACTIVITY_NAME_TRANSLATE} from 'constants/session';
import {themeConfig} from 'constants/theme';
import {UserSessionContext} from 'context';
import {WebRTCServerSource} from 'enums';
import {usePrevious, useUserSession} from 'hooks';
import {AgentSession, SessionActivityName, AgentSessionSettings} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {InSessionDataStorage} from 'services';
import {getActivityIcon, mediaDevicesUtils, sessionUtils, userAuthUtils} from 'utils';

import {GoalName} from '../GoalName';
import {SidebarView} from '../types';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE;

enum MoreActionName {
    Reassign = 'reassign',
    TextChatHistory = 'text-chat-history',
    Transcriptions = 'transcriptions',
    Notes = 'notes',
    OnHold = 'on-hold',
    AudioVideo = 'audio-video',
    Audio = 'audio',
    TextChat = 'text-chat',
    SIP = 'sip',
    AvatarHistory = 'avatar-history',
    ScreenShare = 'screen-share',
    CoBrowsing = 'co-browsing',
    Screenshots = 'screenshots',
    VirtualBackground = 'virtual-background',
    MediaDevices = 'media-devices',
    Deescalate = 'deescalate',
    PreSessionFlow = 'pre-session-flow',
    AgentChatbot = 'agent-chatbot',
}

type MoreAction = {
    name: MoreActionName;
    tKey: string;
    icon: IconName;
    className?: string;
    isAvailable: (params: {session: AgentSession, settings: AgentSessionSettings}) => boolean;
    isDisabled?: (params: {session: AgentSession, settings: AgentSessionSettings}) => boolean;
    isActive?: (params: {session: AgentSession, settings: AgentSessionSettings, sidebarView: SidebarView}) => boolean;
    onClick: () => void;
}

type MoreActionsProps = {
    isOnHold: boolean;
    canManageSession: boolean;
    isMobileSdkSession: boolean;
    toggleOnHold: () => void;
    reassign: () => void;
    toggleActivity: (activityName: SessionActivityName) => void;
    onSidebarViewChange: (view: SidebarView) => void;
    setMediaDevicesDialogOpen: (value: boolean) => void;
    setDeescalateDialogOpen: (value: boolean) => void;
}

const isDisabledActivity = (session: AgentSession, activityName: SessionActivityName) =>
    includes(session.unsupportedActivities, activityName);

const isAvailableActivity = (session: AgentSession, activityName: SessionActivityName) =>
    includes(session.possibleActivities, activityName) || isDisabledActivity(session, activityName);

const hasActiveClientParticipant = (session: AgentSession) => {
    const client = sessionUtils.getClientParticipant(session);
    return !!client && !client.left;
};

const getMoreActions: (props: MoreActionsProps) => MoreAction[] =
 ({
     isOnHold, canManageSession, isMobileSdkSession, toggleOnHold, reassign, toggleActivity,
     onSidebarViewChange, setMediaDevicesDialogOpen, setDeescalateDialogOpen
 }) => [
     {
         name: MoreActionName.MediaDevices,
         tKey: T_KEYS.ACTIONS.MEDIA_DEVICES_MENU,
         icon: 'media-settings',
         className: 'md:tw-hidden',
         isAvailable: ({session}) => mediaDevicesUtils.canChangeMediaSettings() &&
          (isAvailableActivity(session, SessionActivityName.Audio) ||
          isAvailableActivity(session, SessionActivityName.AudioVideo)),
         onClick: () => setMediaDevicesDialogOpen(true),
     },
     {
         name: MoreActionName.OnHold,
         tKey: T_KEYS.ACTIONS.HOLD_SESSION,
         icon: 'phone-pause',
         className: 'md:tw-hidden',
         isAvailable: ({settings}) => settings?.session?.onHold?.enabled && canManageSession,
         isActive: () => isOnHold,
         onClick: toggleOnHold,
     },
     {
         name: MoreActionName.AudioVideo,
         tKey: ACTIVITY_NAME_TRANSLATE[SessionActivityName.AudioVideo],
         icon: getActivityIcon(SessionActivityName.AudioVideo),
         isAvailable: ({session}) => isAvailableActivity(session, SessionActivityName.AudioVideo) && canManageSession,
         isDisabled: ({session}) => isDisabledActivity(session, SessionActivityName.AudioVideo),
         isActive: ({session}) => some(session.activities, {name: SessionActivityName.AudioVideo}),
         onClick: () => toggleActivity(SessionActivityName.AudioVideo),
     },
     {
         name: MoreActionName.Audio,
         tKey: ACTIVITY_NAME_TRANSLATE[SessionActivityName.Audio],
         icon: getActivityIcon(SessionActivityName.Audio),
         isAvailable: ({session}) => isAvailableActivity(session, SessionActivityName.Audio) && canManageSession,
         isDisabled: ({session}) => isDisabledActivity(session, SessionActivityName.Audio),
         isActive: ({session}) => some(session.activities, {name: SessionActivityName.Audio}),
         onClick: () => toggleActivity(SessionActivityName.Audio),
     },
     {
         name: MoreActionName.TextChat,
         tKey: ACTIVITY_NAME_TRANSLATE[SessionActivityName.TextChat],
         icon: getActivityIcon(SessionActivityName.TextChat),
         isAvailable: ({session}) => isAvailableActivity(session, SessionActivityName.TextChat) && canManageSession,
         isDisabled: ({session}) => isDisabledActivity(session, SessionActivityName.TextChat),
         isActive: ({session}) => some(session.activities, {name: SessionActivityName.TextChat}),
         onClick: () => toggleActivity(SessionActivityName.TextChat),
     },
     {
         name: MoreActionName.AgentChatbot,
         tKey: ACTIVITY_NAME_TRANSLATE[SessionActivityName.AgentChatbot],
         icon: getActivityIcon(SessionActivityName.AgentChatbot),
         isAvailable: ({settings}) => settings?.session?.agentChatbot?.enabled && canManageSession && !isMobileSdkSession,
         isActive: ({session}) => some(session.activities, {name: SessionActivityName.AgentChatbot}),
         onClick: () => toggleActivity(SessionActivityName.AgentChatbot),
     },
     {
         name: MoreActionName.SIP,
         tKey: ACTIVITY_NAME_TRANSLATE[SessionActivityName.SIP],
         icon: getActivityIcon(SessionActivityName.SIP),
         isAvailable: ({session}) =>
             some(session.activities, {name: SessionActivityName.SIP}) &&
            isAvailableActivity(session, SessionActivityName.SIP) && canManageSession,
         isDisabled: ({session}) => isDisabledActivity(session, SessionActivityName.SIP),
         isActive: ({session}) => some(session.activities, {name: SessionActivityName.SIP}),
         onClick: () => toggleActivity(SessionActivityName.SIP),
     },
     {
         name: MoreActionName.ScreenShare,
         tKey: ACTIVITY_NAME_TRANSLATE[SessionActivityName.ScreenSharing],
         icon: getActivityIcon(SessionActivityName.ScreenSharing),
         isAvailable: ({session}) => isAvailableActivity(session, SessionActivityName.ScreenSharing) && canManageSession,
         isDisabled: ({session}) => isDisabledActivity(session, SessionActivityName.ScreenSharing),
         isActive: ({session}) => some(session.activities, {name: SessionActivityName.ScreenSharing}),
         onClick: () => toggleActivity(SessionActivityName.ScreenSharing),
     },
     {
         name: MoreActionName.CoBrowsing,
         tKey: ACTIVITY_NAME_TRANSLATE[SessionActivityName.CoBrowsing],
         icon: getActivityIcon(SessionActivityName.CoBrowsing),
         isAvailable: ({session}) => isAvailableActivity(session, SessionActivityName.CoBrowsing) && canManageSession,
         isDisabled: ({session}) => userAuthUtils.isAndroidSDKAuthSource() || isDisabledActivity(session, SessionActivityName.CoBrowsing),
         isActive: ({session}) => some(session.activities, {name: SessionActivityName.CoBrowsing}),
         onClick: () => toggleActivity(SessionActivityName.CoBrowsing),
     },
     {
         name: MoreActionName.Reassign,
         tKey: T_KEYS.ACTIONS.REASSIGN,
         icon: 'phone-forward',
         isAvailable: ({settings}) => settings?.session?.reassignment?.enabled && canManageSession,
         isDisabled: ({session}) => !hasActiveClientParticipant(session),
         onClick: reassign,
     },
     {
         name: MoreActionName.TextChatHistory,
         tKey: T_KEYS.ACTIONS.TEXT_CHAT_HISTORY,
         icon: 'text-chat-history',
         isAvailable: ({settings}) => settings?.userSession?.textChatHistory?.enabled && canManageSession,
         isActive: ({sidebarView}) => sidebarView === SidebarView.TextChatHistory,
         onClick: () => onSidebarViewChange(SidebarView.TextChatHistory)
     },
     {
         name: MoreActionName.AvatarHistory,
         tKey: T_KEYS.ACTIONS.AVATAR_HISTORY,
         icon: 'avatar-history',
         isAvailable: ({settings}) => settings?.userSession?.avatarHistory?.enabled && canManageSession,
         isActive: ({sidebarView}) => sidebarView === SidebarView.AvatarHistory,
         onClick: () => onSidebarViewChange(SidebarView.AvatarHistory)
     },
     {
         name: MoreActionName.PreSessionFlow,
         tKey: T_KEYS.PRE_SESSION_FLOW.TITLE,
         icon: 'checklist',
         isAvailable: ({session}) => some(session.activities, (a) =>
             (a.name === SessionActivityName.TextChat || a.name === SessionActivityName.Avatar) &&
             !!(a.data as any)?.preSessionFlow),
         isActive: ({sidebarView}) => sidebarView === SidebarView.PreSessionFlow,
         onClick: () => onSidebarViewChange(SidebarView.PreSessionFlow)
     },
     {
         name: MoreActionName.Transcriptions,
         tKey: T_KEYS.ACTIONS.TRANSCRIPTIONS,
         icon: 'book-open',
         isAvailable: ({session}) => {
             const activeActivity = find(session.activities, (activity) => [
                 SessionActivityName.AudioVideo,
                 SessionActivityName.Audio
             ].includes(activity.name));

             return !!activeActivity?.data?.streamAudio;
         },
         isActive: ({sidebarView}) => sidebarView === SidebarView.Transcriptions,
         onClick: () => onSidebarViewChange(SidebarView.Transcriptions)
     },
     {
         name: MoreActionName.Screenshots,
         tKey: T_KEYS.ACTIONS.SCREENSHOTS,
         icon: 'screenshots',
         isAvailable: ({session}) => !isEmpty(session.screenshots),
         isActive: ({sidebarView}) => sidebarView === SidebarView.Screenshots,
         onClick: () => onSidebarViewChange(SidebarView.Screenshots),
     },
     {
         name: MoreActionName.VirtualBackground,
         tKey: T_KEYS.ACTIONS.VIRTUAL_BACKGROUND,
         icon: 'virtual-background',
         isAvailable: ({session, settings}) => {
             if (!VideoProcessors.isSupported || !settings.userSession.virtualBackground?.enabled) {
                 return false;
             }

             const activity = find(session.activities, {name: SessionActivityName.AudioVideo});
             return !isEmpty(sessionUtils.filterWebRTCActivitiesBySource([activity], WebRTCServerSource.Twilio));
         },
         isActive: ({sidebarView}) => sidebarView === SidebarView.VirtualBackground,
         onClick: () => onSidebarViewChange(SidebarView.VirtualBackground),
     },
     {
         name: MoreActionName.Notes,
         tKey: T_KEYS.ACTIONS.NOTES,
         icon: 'session-notes',
         isAvailable: ({settings}) => settings?.session?.notes?.enabled && canManageSession,
         isActive: ({sidebarView}) => sidebarView === SidebarView.Notes,
         onClick: () => onSidebarViewChange(SidebarView.Notes),
     },
     {
         name: MoreActionName.Deescalate,
         tKey: T_KEYS.ACTIONS.DEESCALATE,
         icon: 'user-x',
         isAvailable: ({settings, session}) => {
             const client = sessionUtils.getClientParticipant(session);
             return canManageSession && settings?.session?.deescalation &&
                (!!client?.data?.inWidget || some(session.activities, {name: SessionActivityName.SIP}));
         },
         isDisabled: ({session}) => !hasActiveClientParticipant(session),
         onClick: () => setDeescalateDialogOpen(true),
     }
 ];

type EndSessionActionProps = {
    content: React.ReactNode;
    iconName: IconName;
    iconColor: IconColor;
    disabled?: boolean;
    onClick: () => void;
}

const EndSessionAction: React.FC<EndSessionActionProps> = ({iconName, iconColor, content, disabled, onClick}: EndSessionActionProps) => (
    <div className='tw-w-1/2 tw-p-1'>
        <Square
            className={cx('tw-bg-light tw-rounded-4xl tw-shadow-lg',
                {'tw-bg-opacity-80 tw-opacity-60': disabled, 'tw-cursor-pointer': !disabled})}
            onClick={disabled ? null : onClick}
        >
            <div className='tw-h-full'>
                <Icon
                    name={iconName}
                    color={disabled ? 'default-dimmed' : iconColor}
                    size='6xl'
                    thickness='sm'
                    className='tw-mx-auto tw-mt-4 tw-mb-2 md:tw-mt-6 md:tw-mb-4'
                />
                <div>
                    {content}
                </div>
            </div>
        </Square>
    </div>
);

type ActionLabelProps = {
    label: string;
    disabled?: boolean;
    mobileOnly?: boolean;
    showLandscapeLabel?: boolean;
    justifyBetween?: boolean;
    className?: string;
    children: React.ReactElement;
    onClick?: (args?: any) => any;
}

const ActionLabel: React.FC<ActionLabelProps> = ({
    label, disabled, mobileOnly, showLandscapeLabel = true, className, children, onClick, justifyBetween
}: ActionLabelProps) => (
    <div
        className={cx('tw-flex-center tw-flex-col tw-text-sm -md:tw-h-16 -lg-landscape:tw-h-14', {
            'md:tw-hidden': mobileOnly, 'tw-cursor-pointer': onClick, 'tw-opacity-30': disabled,
            '-lg-landscape:tw-h-auto ': !showLandscapeLabel,
            '-md:tw-justify-start': !justifyBetween, '-md:tw-justify-between': justifyBetween
        }, className)}
        onClick={onClick}
    >
        {children}
        <div className={cx('md:tw-hidden tw-text-center', {
            '-lg-landscape:tw-hidden': !showLandscapeLabel,
            '-md:tw-mt-2': !justifyBetween
        })}
        >
            {label}
        </div>
    </div>
);

export const InSessionBottomPanel: React.FC = () => {
    const {t} = useTranslation();
    const context = useContext(UserSessionContext);
    const {
        session,
        settings,
        sidebarView,
        audioEnabled,
        videoEnabled,
        goalToConfirm,
        hasUnreadMessages,
        confirmGoal,
        setGoalToConfirm,
        onToggleAudio,
        onToggleVideo,
        reassign,
        toggleActivity,
        toggleOnHold,
        onSidebarViewChange,
        requestEndSession,
        leaveSession,
        toggleGoalsModal,
        setMediaDevicesDialogOpen,
        setDeescalateDialogOpen
    } = context;

    const {
        isOnHold, isOnHoldEnabled, audioVideoActivity, audioActivity, textChatActivity, sipActivity, goals, isStarted, prevSipActivity,
        canManageSession, agentChatbotActivity, isMobileSdkSession
    } = useUserSession();

    const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
    const [moreTooltipOpen, setMoreTooltipOpen] = useState(false);
    const willRetryMoreTooltip = useRef<boolean>(false);
    const prevSidebarView = usePrevious(sidebarView);
    const completedGoalsCount = useMemo(() => filter(goals, {completed: true}).length, [goals]);
    const allGoalsCompleted = useMemo(() => completedGoalsCount === goals.length, [goals, completedGoalsCount]);

    const toggleMoreMobile = useCallback(() => {
        setMobileActionsOpen((prev) => !prev);
    }, []);

    const renderAudio = useCallback(() => !isOnHold && (audioVideoActivity || audioActivity || sipActivity) &&
        <ActionLabel
            label={t(audioEnabled ? T_KEYS.ACTIONS.MUTE_OFF : T_KEYS.ACTIONS.MUTE_ON)}
            showLandscapeLabel={false}
            className='md:tw-mr-4'
            justifyBetween
            onClick={onToggleAudio}
        >
            <Icon name={audioEnabled ? 'microphone-on' : 'microphone-off'} size='xl' button />
        </ActionLabel>,
    [t, isOnHold, audioVideoActivity, audioActivity, sipActivity, audioEnabled, onToggleAudio]);

    const renderVideo = useCallback(() => !isOnHold && audioVideoActivity &&
        <ActionLabel
            label={t(videoEnabled ? T_KEYS.ACTIONS.VIDEO_OFF : T_KEYS.ACTIONS.VIDEO_ON)}
            showLandscapeLabel={false}
            className='md:tw-mr-4'
            justifyBetween
            onClick={onToggleVideo}
        >
            <Icon name={videoEnabled ? 'video-on' : 'video-off'} size='xl' button />
        </ActionLabel>,
    [t, isOnHold, audioVideoActivity, videoEnabled, onToggleVideo]);

    const renderMediaDevicesSettings = useCallback(() => mediaDevicesUtils.canChangeMediaSettings() &&
    (isAvailableActivity(session, SessionActivityName.AudioVideo) || isAvailableActivity(session, SessionActivityName.Audio)) &&
        <ActionLabel
            label={t(T_KEYS.ACTIONS.MEDIA_DEVICES_MENU)}
            showLandscapeLabel={false}
            justifyBetween
            className='-md:tw-hidden'
            onClick={() => setMediaDevicesDialogOpen(true)}
        >
            <Icon name='media-settings' size='xl' button />
        </ActionLabel>,
    [session, setMediaDevicesDialogOpen, t]);

    const renderEndSessionButton = useCallback((props?: {label?: string; onClick?: () => void}) => (
        <Fragment>
            <Button
                rounded
                color='unstyled'
                Icon={<Icon name='phone-end' size='xl' className='tw-mr-2' />}
                testAttribute={{id: 'end-session-button'}}
                className='tw-bg-dark tw-text-light -md:tw-hidden'
                onClick={props?.onClick}
            >
                {props?.label || t(T_KEYS.ACTIONS.END_SESSION)}
            </Button>
            <ActionLabel
                label={props?.label || t(T_KEYS.ACTIONS.END_SESSION)}
                mobileOnly
                justifyBetween
                showLandscapeLabel={false}
                onClick={props?.onClick}
            >
                <Button
                    width='auto'
                    height='auto'
                    rounded
                    color='unstyled'
                    className='tw-bg-dark tw-text-light'
                >
                    <Icon name='phone-end' size='xl' margin={false} />
                </Button>
            </ActionLabel>
        </Fragment>
    ), [t]);

    const renderEndSessionDropdown = useCallback(() => (
        <div>
            <Dropdown
                position='top'
                distance={30}
                transparent
                triggerNode={renderEndSessionButton()}
            >
                {({onClose}) => (
                    <div className='tw-flex tw-w-screen tw-max-w-72 md:tw-max-w-80'>
                        <EndSessionAction
                            iconName='x-circle'
                            iconColor='alert'
                            content={t(T_KEYS.ACTIONS.REJECT)}
                            onClick={() => {
                                onClose();
                                requestEndSession({success: false});
                            }}
                        />
                        <EndSessionAction
                            disabled={!allGoalsCompleted}
                            iconName='check-circle'
                            iconColor='success'
                            content={
                                <div>
                                    {t(T_KEYS.ACTIONS.SUCCESS)}
                                    {!allGoalsCompleted &&
                                        <div
                                            className='tw-text-primary tw-text-sm tw-cursor-pointer'
                                            onClick={() => {
                                                onClose();
                                                toggleGoalsModal();
                                            }}
                                        >
                                            {`(${t(T_KEYS.LABELS.GOALS)} ${completedGoalsCount} 
                                                ${t(T_KEYS.LABELS.OF)} ${goals.length})`}
                                        </div>}
                                </div>
                            }
                            onClick={() => {
                                onClose();
                                requestEndSession({success: true});
                            }}
                        />
                    </div>
                )}
            </Dropdown>
        </div>
    ), [allGoalsCompleted, completedGoalsCount, goals, renderEndSessionButton, requestEndSession, t, toggleGoalsModal]);

    const renderEndSession = useCallback(() => {
        if (!isStarted) {
            return renderEndSessionButton({onClick: () => requestEndSession(null)});
        }
        if (!canManageSession) {
            return renderEndSessionButton({label: t(T_KEYS.ACTIONS.LEAVE_SESSION), onClick: () => leaveSession()});
        }
        return renderEndSessionDropdown();
    }, [renderEndSessionDropdown, isStarted, renderEndSessionButton, requestEndSession, leaveSession, canManageSession, t]);

    const renderOnHold = useCallback(() => isOnHoldEnabled && canManageSession &&
        <Icon
            name='phone-pause'
            primary={isOnHold}
            size='xl'
            button
            margin={false}
            className='-md:tw-hidden tw-mr-4'
            onClick={toggleOnHold}
        />,
    [toggleOnHold, isOnHold, isOnHoldEnabled, canManageSession]);

    const renderChat = useCallback(() => (
        textChatActivity &&
            <ActionLabel
                label={t(ACTIVITY_NAME_TRANSLATE[SessionActivityName.TextChat])}
                showLandscapeLabel={false}
                justifyBetween
                className='md:tw-ml-4'
                onClick={() => onSidebarViewChange(SidebarView.TextChat)}
            >
                <Icon
                    name={hasUnreadMessages ? 'chat-unread' : 'chat2'}
                    size='xl'
                    button
                    primary={sidebarView === SidebarView.TextChat}
                />
            </ActionLabel>
    ), [t, hasUnreadMessages, textChatActivity, sidebarView, onSidebarViewChange]);

    const renderAgentChatbot = useCallback(() => (
        agentChatbotActivity && canManageSession &&
            <ActionLabel
                label={t(ACTIVITY_NAME_TRANSLATE[SessionActivityName.AgentChatbot])}
                showLandscapeLabel={false}
                justifyBetween
                className='md:tw-ml-4'
                onClick={() => onSidebarViewChange(SidebarView.AgentChatbot)}
            >
                <Icon
                    name={getActivityIcon(SessionActivityName.AgentChatbot)}
                    size='xl'
                    button
                    primary={sidebarView === SidebarView.AgentChatbot}
                />
            </ActionLabel>
    ), [agentChatbotActivity, canManageSession, t, sidebarView, onSidebarViewChange]);

    const renderSip = useCallback(() => (
        (sipActivity || prevSipActivity) &&
            <ActionLabel
                label={t(ACTIVITY_NAME_TRANSLATE[SessionActivityName.SIP])}
                showLandscapeLabel={false}
                justifyBetween
                className='md:tw-ml-4'
                onClick={() => onSidebarViewChange(SidebarView.SIP)}
            >
                <Icon
                    name='phone-history'
                    size='xl'
                    button
                    primary={sidebarView === SidebarView.SIP}
                />
            </ActionLabel>
    ), [sipActivity, t, sidebarView, prevSipActivity, onSidebarViewChange]);

    const moreActions: MoreAction[] = useMemo(() =>
        filter(
            getMoreActions({
                isOnHold,
                canManageSession,
                isMobileSdkSession,
                toggleOnHold,
                reassign,
                toggleActivity,
                onSidebarViewChange,
                setMediaDevicesDialogOpen,
                setDeescalateDialogOpen,
            }),
            ({isAvailable}) => isAvailable({session, settings})),
    [
        session, settings, isOnHold, canManageSession, isMobileSdkSession,
        toggleOnHold, reassign, toggleActivity, onSidebarViewChange, setMediaDevicesDialogOpen,
        setDeescalateDialogOpen,
    ]);

    const renderMoreDesktop = useCallback(() => (
        <TestAttribute testAttribute={{id: 'more-activities-dropdown'}}>
            <div className='-md:tw-hidden tw-ml-4'>
                <Dropdown
                    distance={40}
                    triggerNode={
                        ({open}) => <Icon name='activities' size='xl' button primary={open} />
                    }
                    childrenClassName='tw-rounded-xl tw-overflow-hidden'
                >
                    {map(moreActions, ({name, tKey, icon, className, isActive, isDisabled, onClick}) => {
                        const active = isActive?.({session, settings, sidebarView});
                        const disabled = isDisabled?.({session, settings});
                        return (
                            <DropdownItem
                                key={name}
                                testAttribute={{id: `more-action-${name}`}}
                                disabled={disabled}
                                className={cx('tw-flex tw-items-center', {'tw-text-primary': active}, className)}
                                onClick={onClick}
                            >
                                <Icon name={icon} size='xl' className='tw-mr-4' primary={active} />
                                {t(tKey)}
                            </DropdownItem>
                        );
                    })}
                </Dropdown>
            </div>
        </TestAttribute>
    ), [t, moreActions, session, settings, sidebarView]);

    const renderMoreButtonMobile = useCallback(() => (
        <ActionLabel label={t(T_KEYS.ACTIONS.ACTIONS)} mobileOnly justifyBetween showLandscapeLabel={false} onClick={toggleMoreMobile}>
            <Icon name='activities' size='xl' button primary={mobileActionsOpen} />
        </ActionLabel>
    ), [t, mobileActionsOpen, toggleMoreMobile]);

    const renderMoreActionsMobile = useCallback(() => (
        mobileActionsOpen &&
            <div className='md:tw-hidden tw-mb-2'>
                <div className='tw-border-t tw-border-dark tw-my-2' />
                <div
                    className={cx('tw-flex tw-items-center tw-overflow-x-auto tw-h-24 -lg-landscape:tw-h-18',
                        moreActions.length < 4 ? 'tw-justify-start' : 'tw-justify-between'
                    )}
                >
                    {map(moreActions, ({name, tKey, icon, className, isActive, isDisabled, onClick}) => {
                        const active = isActive?.({session, settings, sidebarView});
                        const disabled = isDisabled?.({session, settings});
                        return (
                            <TestAttribute key={name} testAttribute={{id: `more-action-${name}`}}>
                                <div
                                    className={cx('tw-flex tw-flex-col tw-items-center tw-min-w-16 tw-text-sm tw-mr-5 last:tw-mr-0',
                                        {'tw-text-primary': active, 'tw-cursor-pointer': !disabled}, className)}
                                    onClick={disabled ? null : onClick}
                                >
                                    <ActionLabel label={t(tKey)} disabled={disabled}>
                                        <Icon name={icon} size='xl' primary={active} />
                                    </ActionLabel>
                                </div>
                            </TestAttribute>
                        );
                    })}
                </div>
            </div>
    ), [t, moreActions, session, settings, sidebarView, mobileActionsOpen]);

    const handleMoreTooltipClose = useCallback(() => {
        setMoreTooltipOpen(false);
        if (!willRetryMoreTooltip.current) {
            const data = InSessionDataStorage.getItem();
            InSessionDataStorage.setItem(merge(data, {showedMoreTooltip: true}));
        }
    }, []);

    const handleResize = useCallback(() => {
        setMoreTooltipOpen(false);
        window.removeEventListener('resize', handleResize);
    }, []);

    const openMoreTooltipIfNeeded = useCallback(() => {
        const data = InSessionDataStorage.getItem();
        if (!data?.showedMoreTooltip) {
            setMoreTooltipOpen(true);
            willRetryMoreTooltip.current = false;

            window.addEventListener('resize', handleResize);
            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [handleResize]);

    useEffect(() => openMoreTooltipIfNeeded(), [openMoreTooltipIfNeeded]);

    useEffect(() => {
        if (sidebarView && moreTooltipOpen && window?.innerWidth < themeConfig.screens.md) {
            willRetryMoreTooltip.current = true;
            setMoreTooltipOpen(false);
        }
    }, [moreTooltipOpen, sidebarView]);

    useEffect(() => {
        if (!sidebarView && prevSidebarView && willRetryMoreTooltip.current) {
            return openMoreTooltipIfNeeded();
        }
    }, [sidebarView, prevSidebarView, openMoreTooltipIfNeeded]);

    useEffect(() => {
        if (mobileActionsOpen && goalToConfirm) {
            setMobileActionsOpen(false);
        }
    }, [mobileActionsOpen, goalToConfirm]);

    return (
        <div className='tw-max-w-screen tw-bg-light tw-px-4 -md:tw-rounded-t-2xl'>
            {goalToConfirm ?
                <div className='tw-max-w-md tw-mx-auto tw-py-4 tw-text-center screenshot-hidden'>
                    <GoalName goal={goalToConfirm} className='tw-justify-center' />
                    <div className='tw-grid tw-grid-cols-2 tw-gap-10 tw-mt-4'>
                        <Button
                            width='full'
                            height='auto'
                            onClick={() => setGoalToConfirm(null)}
                        >
                            {t(T_KEYS.ACTIONS.CANCEL)}
                        </Button>
                        <Button
                            color='primary'
                            width='full'
                            height='auto'
                            onClick={confirmGoal}
                        >
                            {t(T_KEYS.ACTIONS.CONFIRM)}
                        </Button>
                    </div>
                </div>
                :
                <Fragment>
                    <div className='md:tw-hidden tw-border-2 tw-border-dark tw-w-11 tw-mx-auto tw-mt-1 tw-rounded-xl' />
                    <div className='tw-h-24 md:tw-h-14 -lg-landscape:tw-h-14 tw-grid tw-grid-cols-3 tw-items-center'>
                        <div className='tw-flex tw-justify-start -md:tw-justify-around'>
                            {renderAudio()}
                            {renderVideo()}
                            {renderMediaDevicesSettings()}
                        </div>
                        <div className='tw-flex-center'>
                            {isStarted && renderOnHold()}
                            {renderEndSession()}
                        </div>
                        <div className='tw-flex tw-justify-end -md:tw-justify-around'>
                            {renderChat()}
                            {renderAgentChatbot()}
                            {renderSip()}
                            {isStarted &&
                                <Tooltip
                                    open={moreTooltipOpen}
                                    disabled={!moreTooltipOpen}
                                    header={t(T_KEYS.ACTIONS_TOOLTIP.HEADER)}
                                    controlled
                                    overlay
                                    message={<div className='tw-w-60 tw-my-2'>{t(T_KEYS.ACTIONS_TOOLTIP.MESSAGE)}</div>}
                                    distance={35}
                                    onClose={handleMoreTooltipClose}
                                >
                                    <Fragment>
                                        {renderMoreDesktop()}
                                        {renderMoreButtonMobile()}
                                    </Fragment>
                                </Tooltip>}
                        </div>
                    </div>
                    {renderMoreActionsMobile()}
                </Fragment>}
        </div>
    );
};
